import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { ListReplacementSTApprovals } from "../../../warehouse_team_management/warehouse_module_repo";
import ReplacementSTView from "../../../warehouse_team_management/stock-transfer/outward_view/ReplacementSTView";
import { ReplacementSTOutward } from "../../../warehouse_team_management/model/warehouse_outward_model";
import LoadingWidget from "../../../../components/loading_spinner";
import { ListPendingReplacementRequests } from "../../../../../service/repos/support/complaint_repo";
import { urls } from "../../../../../utils/urls";

interface PendingReplacementRequest {
  id: string;
  complaint_id: string;
  created_by_name?: string;
  requested_at?: string;
  device_details?: { device_id: string }[];
}

const ReplacementSTApprovalsTab: React.FC = () => {
  const [rows, setRows] = useState<ReplacementSTOutward[]>([]);
  const [requests, setRequests] = useState<PendingReplacementRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<ReplacementSTOutward | null>(null);

  const load = async () => {
    setLoading(true);
    const [stRes, reqRes] = await Promise.all([
      ListReplacementSTApprovals(),
      ListPendingReplacementRequests(),
    ]);
    if (stRes?.success) setRows(stRes.data ?? []);
    if (reqRes?.success) setRequests(reqRes.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const nothingToShow = !loading && rows.length === 0 && requests.length === 0;

  return (
    <div id="complaint_listing_replacement_st_approvals_tab">
      <LoadingWidget loading={loading} />

      {nothingToShow && (
        <p className="text-center text-muted-foreground mt-10">
          Nothing awaiting your approval.
        </p>
      )}

      {requests.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold mb-2">
            Replacement Requests — Pending Approval
          </h3>
          <div className="overflow-x-auto rounded-md border">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left">
                  <th className="px-4 py-2">Complaint ID</th>
                  <th className="px-4 py-2">Requested By</th>
                  <th className="px-4 py-2">Requested At</th>
                  <th className="px-4 py-2">Devices</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <tr
                    key={req.id}
                    id={`complaint_listing_pending_request_row_${req.id}`}
                    className="border-t hover:bg-gray-50"
                  >
                    <td className="px-4 py-2">
                      <NavLink
                        to={`${urls.complaint_detail}?id=${req.complaint_id}`}
                        className="text-blue-600 hover:underline font-mono"
                      >
                        {req.complaint_id}
                      </NavLink>
                    </td>
                    <td className="px-4 py-2">{req.created_by_name || "-"}</td>
                    <td className="px-4 py-2">
                      {req.requested_at
                        ? new Date(req.requested_at).toLocaleString()
                        : "-"}
                    </td>
                    <td className="px-4 py-2">
                      {(req.device_details ?? []).length}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">
            Replacement STs — HW / Business Approval Pending
          </h3>
          <div className="overflow-x-auto rounded-md border">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left">
                  <th className="px-4 py-2">ST ID</th>
                  <th className="px-4 py-2">Complaint ID</th>
                  <th className="px-4 py-2">Customer</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Devices</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    id={`complaint_listing_replacement_st_approvals_row_${row.id}`}
                    className="border-t hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelected(row)}
                  >
                    <td className="px-4 py-2 font-mono">{row.id}</td>
                    <td className="px-4 py-2">{row.complaint_id || "-"}</td>
                    <td className="px-4 py-2">{row.customer_name || "-"}</td>
                    <td className="px-4 py-2">
                      <span className="rounded-full bg-yellow-100 text-yellow-800 px-2.5 py-0.5 text-xs font-medium">
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {(row.faulty_items ?? []).reduce(
                        (n, it) => n + (it.deviceIds?.length ?? 0),
                        0,
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected && (
        <ReplacementSTView
          open={!!selected}
          outward={selected}
          onClose={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default ReplacementSTApprovalsTab;
